import { getMetricTypeFriendlyName } from 'features/reports/functions/metric-type-functions';
import { DateTime } from 'luxon';
import { getSymbolFromCurrency } from 'features/currency/currencySymbolMapHelper';
import { MetricTypes } from 'common/constants/metric-types';
import { AdvancedReportExport } from '../advanced-reports-table.types';
import { KeyValuePair } from 'common/types/key-value-pair';
import Button from 'common/components/buttons/button';
import { ButtonTheme } from 'common/components/buttons/button-theme';
import { useRef, useState } from 'react';
import { CSVLink } from 'react-csv';

function formatDate(date: Date): string {
  return DateTime.fromJSDate(date).toFormat('yyyy-MM-DD');
}

function formatNumberCount(count: number): string {
  return Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(count);
}

function formatNumberToCurrency(cost: number, currency: string): string {
  const formattedCost = Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(cost);

  return `${currency}${formattedCost}`;
}

interface AdvancedReportsCsvExportButtonProps {
  exportRequest: AdvancedReportExport;
  isDisabled: boolean;
}

const AdvancedReportCsvExportButton = ({
  exportRequest,
  isDisabled
}: AdvancedReportsCsvExportButtonProps) => {
  const [csvData, setCsvData] = useState<string[][]>([]);

  const startDateShortIso = formatDate(exportRequest.startDate);
  const endDateShortIso = formatDate(exportRequest.endDate);

  const filename = `AdvancedReport_${startDateShortIso}_${endDateShortIso}_${exportRequest.attributionModel}.csv`;

  const currencySymbol = getSymbolFromCurrency(exportRequest.currencyCode);

  const triggerDownload = () => {
    const rowsAsKeyValues = exportRequest.rows.map<
      KeyValuePair<string, string>[]
    >((row) => [
      {
        key: 'Field',
        value: row.id
      },
      {
        key: MetricTypes.IMPRESSIONS,
        value: formatNumberCount(row.impressions.primaryValue)
      },
      {
        key: MetricTypes.CLICKS,
        value: formatNumberCount(row.clicks.primaryValue)
      },
      {
        key: MetricTypes.INBOUND_CALL,
        value: formatNumberCount(row.call.primaryValue)
      },
      {
        key: MetricTypes.WEB,
        value: formatNumberCount(row.web.primaryValue)
      },
      {
        key: MetricTypes.OPPORTUNITIES,
        value: formatNumberCount(row.opportunity.primaryValue)
      },
      {
        key: MetricTypes.OPPORTUNITY_VALUES,
        value: formatNumberToCurrency(
          row.opportunityValue.primaryValue,
          currencySymbol
        )
      },
      {
        key: MetricTypes.REVENUES,
        value: formatNumberCount(row.sales.primaryValue)
      },
      {
        key: MetricTypes.REVENUE_VALUES,
        value: formatNumberToCurrency(row.revenue.primaryValue, currencySymbol)
      },
      {
        key: MetricTypes.COST,
        value: formatNumberToCurrency(row.cost.primaryValue, currencySymbol)
      },
      {
        key: MetricTypes.CPA,
        value: formatNumberToCurrency(row.cpa.primaryValue, currencySymbol)
      },
      {
        key: MetricTypes.CPS,
        value: formatNumberToCurrency(row.cps.primaryValue, currencySymbol)
      },
      {
        key: MetricTypes.ROI,
        value: formatNumberToCurrency(row.roi.primaryValue, currencySymbol)
      },
      {
        key: MetricTypes.ROAS,
        value: Intl.NumberFormat('en-GB', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(row.roas.primaryValue)
      }
    ]);

    const rowsInColumnDefOrder = rowsAsKeyValues.map((rawRow) =>
      exportRequest.orderedColumnIds.map(
        (columnId) => rawRow.find((cell) => cell.key === columnId)!.value
      )
    );

    const friendlyOrderedHeaders = exportRequest.orderedColumnIds.map(
      (header) => getMetricTypeFriendlyName(header)
    );

    const newCsvData = [friendlyOrderedHeaders, ...rowsInColumnDefOrder];

    setCsvData(newCsvData);

    csvDownloadRef.current?.click();
  };

  const csvDownloadRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <Button
        theme={ButtonTheme.CANCEL}
        identifier="export"
        onClick={triggerDownload}
        isLoading={false}
        isDisabled={isDisabled}
      >
        <div className="flex items-center justify-between">
          <i className="ra-icons-generic-download text-sm mr-2 flex"></i>
          Export
        </div>
      </Button>
      {csvData.length > 0 ? (
        <CSVLink data={csvData} filename={filename} className="hidden">
          <button ref={csvDownloadRef}></button>
        </CSVLink>
      ) : (
        <></>
      )}
    </>
  );
};

export default AdvancedReportCsvExportButton;
